import type { LE_AGENT_HUB_HOTEL_BUNDLED_FLIGHT_ALLOWED_COUNTRIES } from 'constants/config/leagenthub'
import { MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG } from 'constants/charterFlight'

export interface PropertyFlightConfigurationParams {
  areFlightsEnabled: boolean
  bundledWithFlightsOnly: boolean
  canBookAllFlights?: boolean
  campaignTags: Array<string>
  isAgentHubEnabled: boolean
  isFlightsGloballyEnabled: boolean
  isOnlyHotelOfferFlightsForMaldivesChartsEnabled: boolean
  location: AgentHubBundledFlightLocationSource
}

const INDONESIA_COUNTRY_TOKEN = 'indonesia'
const INDONESIA_COUNTRY_CODE = 'ID'
const INDONESIA_ALLOWED_LOCATION_TOKENS = new Set(['bali'])

const AGENT_HUB_BUNDLED_FLIGHT_ALLOWED_LOCATIONS = [
  { country: 'singapore', countryCode: 'SG' },
  { country: 'vietnam', countryCode: 'VN' },
  { country: 'malaysia', countryCode: 'MY' },
  { country: 'maldives', countryCode: 'MV' },
  { country: 'new zealand', countryCode: 'NZ' },
  { country: 'hong kong', countryCode: 'HK' },
  { country: 'japan', countryCode: 'JP' },
] as const satisfies ReadonlyArray<{
  country: Exclude<(typeof LE_AGENT_HUB_HOTEL_BUNDLED_FLIGHT_ALLOWED_COUNTRIES)[number], typeof INDONESIA_COUNTRY_TOKEN>
  countryCode: string
}>

const AGENT_HUB_BUNDLED_FLIGHT_ALLOWED_COUNTRIES = new Set<string>(
  AGENT_HUB_BUNDLED_FLIGHT_ALLOWED_LOCATIONS.map(location => location.country),
)

const AGENT_HUB_BUNDLED_FLIGHT_ALLOWED_COUNTRY_CODES = new Set<string>(
  AGENT_HUB_BUNDLED_FLIGHT_ALLOWED_LOCATIONS.map(location => location.countryCode),
)

export interface AgentHubBundledFlightLocationSource {
  countryCode?: string | null
  locationCandidates: Array<string | null | undefined>
}

export function normalizeLocationToken(value?: string): string | undefined {
  if (!value) {
    return undefined
  }

  return value
    .toLowerCase()
    .replace(/[^\w\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim() || undefined
}

export function splitLocationTokens(value?: string): Array<string> {
  if (!value) {
    return []
  }

  const splitValues = value
    .split(/[,/|]/)
    .flatMap(locationPart => {
      const normalizedLocationPart = normalizeLocationToken(locationPart)
      if (!normalizedLocationPart) {
        return []
      }

      return [normalizedLocationPart, ...normalizedLocationPart.split(/\s+/)]
    })
    .filter(Boolean)

  return Array.from(new Set(splitValues))
}

export function isAgentHubBundledFlightsLocationWhitelisted({
  countryCode,
  locationCandidates,
}: AgentHubBundledFlightLocationSource): boolean {
  const normalizedCountryCode = countryCode?.toUpperCase()
  if (normalizedCountryCode && AGENT_HUB_BUNDLED_FLIGHT_ALLOWED_COUNTRY_CODES.has(normalizedCountryCode)) {
    return true
  }

  const locationTokens = locationCandidates.flatMap(splitLocationTokens)
  if (locationTokens.some(locationToken => AGENT_HUB_BUNDLED_FLIGHT_ALLOWED_COUNTRIES.has(locationToken))) {
    return true
  }

  const hasIndonesiaLocationAlias = locationTokens.some(locationToken => INDONESIA_ALLOWED_LOCATION_TOKENS.has(locationToken))
  const hasIndonesiaSignal = normalizedCountryCode === INDONESIA_COUNTRY_CODE ||
    locationTokens.includes(INDONESIA_COUNTRY_TOKEN) ||
    hasIndonesiaLocationAlias
  if (hasIndonesiaSignal) {
    return hasIndonesiaLocationAlias
  }

  return false
}

export function getPropertyFlightConfiguration({
  areFlightsEnabled,
  bundledWithFlightsOnly,
  canBookAllFlights,
  campaignTags,
  isAgentHubEnabled,
  isFlightsGloballyEnabled,
  isOnlyHotelOfferFlightsForMaldivesChartsEnabled,
  location,
}: PropertyFlightConfigurationParams): Pick<App.Property.Property, 'areFlightsEnabled' | 'mustBundleWithFlights'> {
  const baseAreFlightsEnabled = isFlightsGloballyEnabled && areFlightsEnabled
  const baseMustBundleWithFlights = !!bundledWithFlightsOnly

  if (!isAgentHubEnabled) {
    return {
      areFlightsEnabled: baseAreFlightsEnabled,
      mustBundleWithFlights: baseMustBundleWithFlights,
    }
  }

  const isDirectMaldivesCharter = campaignTags.includes(MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG)
  const isLocationEligible = isAgentHubBundledFlightsLocationWhitelisted(location)
  const areAgentHubFlightsEnabled = baseAreFlightsEnabled && (
    !isOnlyHotelOfferFlightsForMaldivesChartsEnabled ||
    isDirectMaldivesCharter
  )
  const shouldDowngradeFlights = !isDirectMaldivesCharter && (
    !canBookAllFlights ||
    !isLocationEligible
  )

  return {
    areFlightsEnabled: shouldDowngradeFlights ? false : areAgentHubFlightsEnabled,
    mustBundleWithFlights: shouldDowngradeFlights ? false : baseMustBundleWithFlights,
  }
}
